import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Brain, Bookmark, Search, ArrowDownRight, ArrowUpRight } from "lucide-react";

/**
 * Bank Auto-Learn Memory Manager (Feb 2026)
 * Two sections:
 *  1. Payer signature → Party — the older narration-based matcher.
 *  2. Party → Counter Account — the newer per-party auto-fill on Create Voucher.
 * Both are lists with per-row Clear so accountants can prune bad mappings.
 */
export default function BankMemoryTab() {
  // ── Section 1 · legacy payer-signature memory ─────────────────────────────
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState({});

  const load = async () => {
    const [m, p] = await Promise.all([
      api.get("/bank/match-memory"),
      api.get("/parties"),
    ]);
    setRows(m.data.rows || []);
    setParties(Object.fromEntries((p.data || []).map(x => [x.id, x.name])));
  };
  useEffect(() => { load(); }, []);

  const forget = async (sig) => {
    if (!window.confirm(`Forget mapping for "${sig}"?`)) return;
    try {
      await api.delete(`/bank/match-memory/${encodeURIComponent(sig)}`);
      toast.success("Mapping forgotten");
      load();
    } catch { toast.error("Failed"); }
  };

  // ── Section 2 · party → counter-account memory ────────────────────────────
  const [pcaRows, setPcaRows] = useState([]);
  const [pcaBusy, setPcaBusy] = useState(false);
  const [pcaQ, setPcaQ] = useState("");
  const [pcaDir, setPcaDir] = useState("all");

  const loadPca = async () => {
    setPcaBusy(true);
    try {
      const r = await api.get("/bank/party-memory/all");
      setPcaRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load counter-account memory");
    } finally {
      setPcaBusy(false);
    }
  };
  useEffect(() => { loadPca(); }, []);

  const forgetPca = async (row) => {
    const partyName = row.party?.name || "this party";
    const dirLabel = row.direction === "out" ? "payments" : "receipts";
    if (!window.confirm(`Forget the counter account remembered for ${partyName} on ${dirLabel}?`)) return;
    try {
      await api.delete("/bank/party-memory", {
        params: { party_id: row.party_id, direction: row.direction },
      });
      toast.success("Forgotten");
      loadPca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const clearAllPca = async () => {
    if (pcaRows.length === 0) return;
    if (!window.confirm(`Clear ALL ${pcaRows.length} learned party→counter-account mappings? This cannot be undone.`)) return;
    try {
      const r = await api.delete("/bank/party-memory/all");
      toast.success(`Cleared ${r.data.deleted} mapping(s)`);
      loadPca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const filteredPca = useMemo(() => {
    const q = pcaQ.trim().toLowerCase();
    return pcaRows.filter((r) => {
      if (pcaDir !== "all" && r.direction !== pcaDir) return false;
      if (!q) return true;
      const hay = [
        r.party?.name, r.counter_account?.name,
        r.counter_account?.code, r.last_narration,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [pcaRows, pcaQ, pcaDir]);

  const dirBadge = (d) => d === "out"
    ? <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-rose-700 bg-rose-100 rounded-sm px-1.5 py-0.5"><ArrowUpRight className="w-3 h-3" />Out · Payment</span>
    : <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-700 bg-emerald-100 rounded-sm px-1.5 py-0.5"><ArrowDownRight className="w-3 h-3" />In · Receipt</span>;

  return (
    <div className="space-y-6" data-testid="bank-memory-tab">
      {/* ─────────────── Section 1 · Payer signature → Party ─────────────── */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
          <Brain className="w-3.5 h-3.5" /> Payer signature → Party
        </div>
        <div className="text-sm text-muted-foreground mt-1">
          Every accepted bank match teaches the system to score similar narrations higher next time. Prune anything wrong here.
        </div>
      </div>

      <Card className="rounded-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/40">
              <tr>
                <th className="p-2 text-left">Payer signature</th>
                <th className="p-2 text-left">Party</th>
                <th className="p-2 text-right">Hits</th>
                <th className="p-2 text-left">Last used</th>
                <th className="p-2 text-left">Source</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground text-sm">
                  No learned mappings yet. Match a few bank entries and I'll start remembering.
                </td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} className="border-t hover:bg-muted/30" data-testid={`bank-mem-row-${r.signature}`}>
                  <td className="p-2 font-mono text-xs">{r.signature}</td>
                  <td className="p-2">{parties[r.party_id] || <span className="text-muted-foreground italic">Unknown party</span>}</td>
                  <td className="p-2 text-right font-mono tabular-nums">{r.hits}</td>
                  <td className="p-2 text-[11px] font-mono text-muted-foreground">{(r.last_used || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2 text-[10px] font-mono uppercase text-muted-foreground">{r.source}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => forget(r.signature)}
                              className="h-7 w-7 p-0 hover:bg-red-500/10 text-red-500" data-testid={`bank-mem-forget-${r.signature}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ─────────────── Section 2 · Party → Counter Account ─────────────── */}
      <div className="pt-4 border-t">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-violet-700 flex items-center gap-1.5">
              <Bookmark className="w-3.5 h-3.5" /> Party → Counter Account
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              When you create a voucher from a bank row for a party, the counter account you pick is remembered here and auto-filled next time. Clear any mapping that led to the wrong account.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={pcaQ} onChange={(e) => setPcaQ(e.target.value)}
                     placeholder="Search party or account…"
                     className="h-8 pl-8 text-xs w-56 rounded-sm"
                     data-testid="bank-mem-pca-search" />
            </div>
            <Select value={pcaDir} onValueChange={setPcaDir}>
              <SelectTrigger className="h-8 text-xs w-36 rounded-sm" data-testid="bank-mem-pca-dir">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All directions</SelectItem>
                <SelectItem value="out">Out · Payments</SelectItem>
                <SelectItem value="in">In · Receipts</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={clearAllPca}
                    disabled={pcaBusy || pcaRows.length === 0}
                    className="h-8 rounded-sm text-xs gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="bank-mem-pca-clear-all">
              <Trash2 className="w-3.5 h-3.5" /> Clear all
            </Button>
          </div>
        </div>
      </div>

      <Card className="rounded-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/40">
              <tr>
                <th className="p-2 text-left">Party</th>
                <th className="p-2 text-left">Direction</th>
                <th className="p-2 text-left">Counter account</th>
                <th className="p-2 text-left">Last narration</th>
                <th className="p-2 text-right">Used</th>
                <th className="p-2 text-left">Last used</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {pcaBusy && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">Loading…</td></tr>
              )}
              {!pcaBusy && filteredPca.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-sm" data-testid="bank-mem-pca-empty">
                  {pcaRows.length === 0
                    ? "No party → counter-account mappings yet. Post a voucher from a bank row and it will start remembering."
                    : "No mappings match this filter."}
                </td></tr>
              )}
              {!pcaBusy && filteredPca.map((r) => (
                <tr key={`${r.party_id}:${r.direction}`} className="border-t hover:bg-muted/30"
                    data-testid={`bank-mem-pca-row-${r.party_id}-${r.direction}`}>
                  <td className="p-2">
                    {r.party?.name || <span className="text-muted-foreground italic">Deleted party</span>}
                    {r.party?.party_type && (
                      <div className="text-[10px] font-mono uppercase text-muted-foreground">{r.party.party_type}</div>
                    )}
                  </td>
                  <td className="p-2">{dirBadge(r.direction)}</td>
                  <td className="p-2">
                    {r.counter_account ? (
                      <>
                        <span className="font-mono text-[11px] mr-1">{r.counter_account.code}</span>
                        {r.counter_account.name}
                        <div className="text-[10px] font-mono uppercase text-muted-foreground">{r.counter_account.account_type}</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground italic">Deleted account</span>
                    )}
                  </td>
                  <td className="p-2 text-xs text-muted-foreground truncate max-w-[240px]" title={r.last_narration}>
                    {r.last_narration || "—"}
                  </td>
                  <td className="p-2 text-right font-mono tabular-nums">{r.count}</td>
                  <td className="p-2 text-[11px] font-mono text-muted-foreground">
                    {(r.last_used_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => forgetPca(r)}
                              className="h-7 w-7 p-0 hover:bg-red-500/10 text-red-500"
                              data-testid={`bank-mem-pca-forget-${r.party_id}-${r.direction}`}
                              title="Forget this mapping">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
